import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

/**
 * Renderizador markdown mínimo, sem dependência externa. Cobre o que os
 * docs do projeto usam: h1-h6, parágrafos, code fenced e inline, listas,
 * tabelas, links, citações e separadores.
 *
 * Extraído de pages/api_tokens/ApiDocsPage.js pra ser usado também pela
 * ajuda do DNS Reverso.
 */
function escapeHtml(s) {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function renderInline(text) {
    // Aplica em ordem: code inline → bold → links
    let out = escapeHtml(text);
    out = out.replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:1px 5px;border-radius:3px;font-size:0.9em">$1</code>');
    out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener" style="color:#e01b29">$1</a>');
    return out;
}

function MarkdownRender({ text }) {
    if (!text) return null;
    const lines = text.split('\n');
    const blocks = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];

        // Code fence
        if (line.startsWith('```')) {
            const lang = line.slice(3).trim();
            const code = [];
            i++;
            while (i < lines.length && !lines[i].startsWith('```')) {
                code.push(lines[i]);
                i++;
            }
            i++;
            blocks.push({ type: 'code', lang, content: code.join('\n') });
            continue;
        }

        // Heading
        const h = line.match(/^(#{1,6})\s+(.+)$/);
        if (h) {
            blocks.push({ type: 'h', level: h[1].length, content: h[2] });
            i++;
            continue;
        }

        // Table (linha com pipes seguida de linha separadora)
        if (line.includes('|') && i + 1 < lines.length && /^[\s|:\-]+$/.test(lines[i + 1])) {
            const header = line.split('|').map((s) => s.trim()).filter((s, idx, arr) => idx > 0 && idx < arr.length - 1 || s);
            i += 2;
            const rows = [];
            while (i < lines.length && lines[i].includes('|') && lines[i].trim()) {
                rows.push(lines[i].split('|').map((s) => s.trim()).filter((s, idx, arr) => idx > 0 && idx < arr.length - 1 || s));
                i++;
            }
            blocks.push({ type: 'table', header, rows });
            continue;
        }

        // List
        if (/^[\s]*[-*]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^[\s]*[-*]\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^[\s]*[-*]\s+/, ''));
                i++;
            }
            blocks.push({ type: 'ul', items });
            continue;
        }
        if (/^\s*\d+\.\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*\d+\.\s+/, ''));
                i++;
            }
            blocks.push({ type: 'ol', items });
            continue;
        }

        // Horizontal rule
        if (/^---+\s*$/.test(line)) {
            blocks.push({ type: 'hr' });
            i++;
            continue;
        }

        // Blockquote
        if (line.startsWith('> ')) {
            const lines2 = [];
            while (i < lines.length && lines[i].startsWith('> ')) {
                lines2.push(lines[i].slice(2));
                i++;
            }
            blocks.push({ type: 'quote', content: lines2.join(' ') });
            continue;
        }

        // Empty line — pular
        if (!line.trim()) {
            i++;
            continue;
        }

        // Parágrafo (junta linhas até vazia)
        const para = [line];
        i++;
        while (i < lines.length && lines[i].trim() && !lines[i].startsWith('#')
               && !lines[i].startsWith('```') && !/^[\s]*[-*]\s+/.test(lines[i])) {
            para.push(lines[i]);
            i++;
        }
        blocks.push({ type: 'p', content: para.join(' ') });
    }

    return (
        <>
            {blocks.map((b, idx) => {
                if (b.type === 'h') {
                    const variants = { 1: 'h4', 2: 'h5', 3: 'h6', 4: 'subtitle1', 5: 'subtitle2', 6: 'body1' };
                    return (
                        <Typography key={idx} variant={variants[b.level] || 'body1'} sx={{
                            mt: b.level <= 2 ? 3 : 2, mb: 1, fontWeight: 600,
                            color: b.level === 1 ? 'primary.main' : 'text.primary',
                        }} dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />
                    );
                }
                if (b.type === 'p') {
                    return (
                        <Typography key={idx} variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}
                            dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />
                    );
                }
                if (b.type === 'code') {
                    return (
                        <Box key={idx} sx={{ position: 'relative', mb: 2 }}>
                            <Box sx={{
                                bgcolor: '#2b2b2b', color: '#f8f8f2', p: 2, borderRadius: 1,
                                fontFamily: 'monospace', fontSize: 12, overflow: 'auto',
                                whiteSpace: 'pre',
                            }}>
                                {b.content}
                            </Box>
                            <Tooltip title="Copiar">
                                <IconButton
                                    size="small"
                                    onClick={() => { navigator.clipboard?.writeText(b.content); }}
                                    sx={{ position: 'absolute', top: 4, right: 4, color: '#aaa' }}
                                >
                                    <ContentCopyIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    );
                }
                if (b.type === 'ul') {
                    return (
                        <Box key={idx} component="ul" sx={{ mb: 1.5, pl: 3 }}>
                            {b.items.map((it, j) => (
                                <li key={j}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}
                                        dangerouslySetInnerHTML={{ __html: renderInline(it) }} />
                                </li>
                            ))}
                        </Box>
                    );
                }
                if (b.type === 'ol') {
                    return (
                        <Box key={idx} component="ol" sx={{ mb: 1.5, pl: 3 }}>
                            {b.items.map((it, j) => (
                                <li key={j}>
                                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}
                                        dangerouslySetInnerHTML={{ __html: renderInline(it) }} />
                                </li>
                            ))}
                        </Box>
                    );
                }
                if (b.type === 'table') {
                    return (
                        <Box key={idx} sx={{ overflow: 'auto', mb: 2 }}>
                            <Box component="table" sx={{
                                width: '100%', borderCollapse: 'collapse',
                                '& th, & td': { border: '1px solid', borderColor: 'divider', p: 1, fontSize: 13 },
                                '& th': { bgcolor: 'grey.100', fontWeight: 600, textAlign: 'left' },
                            }}>
                                <thead>
                                    <tr>{b.header.map((h, j) => <th key={j} dangerouslySetInnerHTML={{ __html: renderInline(h) }} />)}</tr>
                                </thead>
                                <tbody>
                                    {b.rows.map((r, j) => (
                                        <tr key={j}>{r.map((c, k) => <td key={k} dangerouslySetInnerHTML={{ __html: renderInline(c) }} />)}</tr>
                                    ))}
                                </tbody>
                            </Box>
                        </Box>
                    );
                }
                if (b.type === 'hr') return <Box key={idx} sx={{ borderTop: '1px solid', borderColor: 'divider', my: 3 }} />;
                if (b.type === 'quote') {
                    return (
                        <Box key={idx} sx={{
                            borderLeft: '3px solid', borderColor: 'primary.main',
                            pl: 2, mb: 2, color: 'text.secondary', fontStyle: 'italic',
                        }}>
                            <Typography variant="body2" dangerouslySetInnerHTML={{ __html: renderInline(b.content) }} />
                        </Box>
                    );
                }
                return null;
            })}
        </>
    );
}

export default MarkdownRender;
export { renderInline, escapeHtml };
